/**
 * rxDosage — the ONE client-side mirror of the parser's dose → frequency rule.
 *
 * THE RULE, quoted verbatim from the server that owns it
 * (`app/services/prescription_parser.py`, "Infer frequency from slot pattern",
 * ~L1672-1679 on the branch this ships with):
 *
 *     # ── Infer frequency from slot pattern ────────────────────────
 *     #    Frequency is how many TIMES a day the patient takes a dose —
 *     #    the count of non-zero slots, never the sum of the quantities.
 *     #    "2-0-2" is 2 tablets twice daily, not "Four Times Daily".
 *     if result["dosage_schedule"] and not result["frequency"]:
 *         doses_per_day = len([q for q in result["dosage_schedule"].values() if q])
 *         freq_infer = {1: "Once Daily", 2: "Twice Daily", 3: "Three Times Daily"}
 *         result["frequency"] = freq_infer.get(doses_per_day)
 *
 * So `2-0-0` is ONE dose a day of two tablets — `Once Daily`. FE-18 shipped the
 * sum instead (it was read off a stale copy of the parser on `master`, not the
 * branch this phase ships with), which printed `Twice Daily` on a once-a-day
 * medicine: a clinical error on the script. FE-21 (verdict R1) counts slots.
 *
 * Why this module exists at all: the parser fills `frequency` from the slots
 * ONCE, at parse time. The row editor let the doctor change M/A/N afterwards
 * without revisiting it, so a row parsed as `1-0-1 → Twice Daily` still printed
 * "Twice Daily" after being edited to `1-0-0`.
 *
 * NEVER-INVENT: a frequency the doctor (or the parser's own shorthand map)
 * wrote — `SOS`, `Weekly`, `Every 8 hours`, `Stat` — is left exactly as it is.
 * Only an EMPTY frequency, or one that is itself one of the three inferable
 * labels, is recomputed. No dose at all (`0-0-0`) has no label, and changes
 * nothing rather than clearing what is on screen.
 */

/** doses per day (the count of non-zero slots) → the parser's label. */
export const INFERRED_FREQUENCY_BY_DOSES_PER_DAY = {
  1: 'Once Daily',
  2: 'Twice Daily',
  3: 'Three Times Daily',
};

/** The only frequencies this helper is allowed to overwrite. */
export const INFERABLE_FREQUENCIES = Object.values(INFERRED_FREQUENCY_BY_DOSES_PER_DAY);

/** A slot counts as a dose when it carries a real, positive quantity. */
const isDose = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0;
};

/** How many TIMES a day this schedule is taken (0-3). */
export function dosesPerDay(schedule) {
  if (!schedule) return 0;
  return [schedule.morning, schedule.afternoon, schedule.night].filter(isDose).length;
}

/** The parser's label for a schedule, or null when it has none. */
export function inferFrequency(schedule) {
  return INFERRED_FREQUENCY_BY_DOSES_PER_DAY[dosesPerDay(schedule)] || null;
}

/**
 * What `frequency` should read after a dose slot changed.
 *
 * @param {{morning, afternoon, night}} schedule  the NEW schedule
 * @param {string|null} currentFrequency          what the row shows today
 * @returns {string|null} the frequency to store (may be `currentFrequency`)
 */
export function frequencyForSchedule(schedule, currentFrequency) {
  const inferred = inferFrequency(schedule);
  if (!inferred) return currentFrequency ?? null;
  const current = String(currentFrequency || '').trim();
  if (!current) return inferred;
  const isInferable = INFERABLE_FREQUENCIES
    .some((label) => label.toLowerCase() === current.toLowerCase());
  return isInferable ? inferred : currentFrequency;
}

/**
 * RX-HALF-DOSE — dose-slot parse/format (½ tablets in a M/A/N slot).
 *
 * The ONE canonical mirror of the backend's `app/core/dose.py`. Every
 * display reader, the pad's stepper input, template apply, Rx memory
 * prefill and the procedure-approval dialog route through
 * `parseDoseSlot` / `formatDoseSlot` / `formatDoseSchedule` /
 * `parseDoseSchedule` — never a bare `Number()` or a hand-built
 * `${m}-${a}-${n}` template literal.
 *
 * `DOSE_STEP` is the single knob: widening it (e.g. to 0.25 for ¼) is a
 * one-line change here plus the mirrored one in `app/core/dose.py` — not
 * done in this phase (owner LOCKED: step 0.5 only).
 *
 * The golden round-trip fixture (`parse(format(x)) === x` over every
 * accepted spelling) is duplicated byte-identically in
 * `tests/unit/test_dose_core.py` on the backend — a change here without
 * the matching backend change is the FE-18-vs-parser class of bug this
 * guards against.
 */
export const DOSE_STEP = 0.5;
export const DOSE_MIN = 0;
export const DOSE_MAX = 9;

const HALF_GLYPH = '½';
const UNICODE_FRACTIONS = { '½': 0.5, '¼': 0.25, '¾': 0.75 };

const MIXED_FRACTION_RE = /^(-?\d+)\s+(\d+)\s*\/\s*(\d+)$/;
const SIMPLE_FRACTION_RE = /^(-?\d+)\s*\/\s*(\d+)$/;
const UNICODE_FRACTION_RE = /^(-?\d*)([½¼¾])$/;
const DECIMAL_RE = /^-?\d*\.\d+$/;
const INT_RE = /^-?\d+$/;

function parseDoseString(raw) {
  const s = String(raw).trim();
  if (!s) return null;

  let m = s.match(MIXED_FRACTION_RE);
  if (m) {
    const denom = Number(m[3]);
    if (!denom) return null;
    return Number(m[1]) + Number(m[2]) / denom;
  }

  m = s.match(SIMPLE_FRACTION_RE);
  if (m) {
    const denom = Number(m[2]);
    if (!denom) return null;
    return Number(m[1]) / denom;
  }

  m = s.match(UNICODE_FRACTION_RE);
  if (m) {
    const whole = (m[1] === '' || m[1] === '-') ? 0 : Number(m[1]);
    return whole + UNICODE_FRACTIONS[m[2]];
  }

  if (DECIMAL_RE.test(s) || INT_RE.test(s)) return Number(s);
  return null;
}

/**
 * Canonical parser for one M/A/N slot — the ONLY place a dose string is
 * turned into a Number. Accepts every spelling `app/core/dose.py`
 * accepts (`1`, `"1"`, `1.5`, `".5"`, `"0.5"`, `"1/2"`, `"½"`, `"1½"`,
 * `"1 1/2"`) and canonicalises to the nearest 0.5 step.
 *
 * Returns `null` for anything off-step, out of range (0..9), or
 * unparseable — callers MUST treat `null` as "reject, keep the field's
 * last valid value" (AC3); never coerce a `null` to `0`.
 */
export function parseDoseSlot(raw) {
  if (raw === null || raw === undefined) return null;
  let value;
  if (typeof raw === 'number') {
    if (!Number.isFinite(raw)) return null;
    value = raw;
  } else if (typeof raw === 'string') {
    value = parseDoseString(raw);
    if (value === null) return null;
  } else {
    return null;
  }
  if (value < DOSE_MIN || value > DOSE_MAX) return null;
  const steps = value / DOSE_STEP;
  const rounded = Math.round(steps);
  if (Math.abs(rounded - steps) > 1e-9) return null;
  return rounded * DOSE_STEP;
}

/** Canonical Number -> display string: `0`->"0", `0.5`->"½", `1`->"1",
 * `1.5`->"1½" — byte-identical to `app/core/dose.py::format_dose_slot`. */
export function formatDoseSlot(value) {
  const v = typeof value === 'number' ? value : parseDoseSlot(value);
  if (v === null || v === undefined || Number.isNaN(v)) return '0';
  const whole = Math.trunc(v);
  const frac = v - whole;
  if (Math.abs(frac) < 1e-9) return String(whole);
  if (Math.abs(frac - DOSE_STEP) < 1e-9) return whole ? `${whole}${HALF_GLYPH}` : HALF_GLYPH;
  // Defensive only — every value on this path was already validated by
  // parseDoseSlot (step 0.5), so this branch is unreachable in practice.
  return String(v);
}

/**
 * Renders a full M/A/N schedule, e.g. `"½-0-½"` — the ONE place a
 * schedule becomes a display string. Byte-identical to the backend's
 * `format_dose_schedule(sched, sep="-")`.
 */
export function formatDoseSchedule(schedule, sep = '-') {
  if (!schedule) return '';
  const m = formatDoseSlot(schedule.morning ?? 0);
  const a = formatDoseSlot(schedule.afternoon ?? 0);
  const n = formatDoseSlot(schedule.night ?? 0);
  return [m, a, n].join(sep);
}

/**
 * Parses a `"½-0-½"` / `"1-0-1"` string back into `{morning, afternoon,
 * night}` Numbers — the ONE place a dosage string is re-split. Returns
 * `null` for an empty or malformed (not-3-part) string, matching the
 * `slotDisplay`/`doseParts.length === 3` guard this helper replaces.
 */
export function parseDoseSchedule(str, sep = '-') {
  if (!str) return null;
  const parts = String(str).split(sep);
  if (parts.length !== 3) return null;
  return {
    morning: parseDoseSlot(parts[0]) ?? 0,
    afternoon: parseDoseSlot(parts[1]) ?? 0,
    night: parseDoseSlot(parts[2]) ?? 0,
  };
}
